import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Eye, Ear, HelpCircle } from 'lucide-react';
import { reachPulseAPI, ReachPulseEntry } from '../../../services/reachService';

interface Props {
  projectId: string;
  week: number;
}

type PulseField = 'saw' | 'heard' | 'needHelp';

const FIELDS = [
  { key: 'saw', label: 'What we saw', icon: Eye, placeholder: 'Behaviours, reactions, participation levels…', color: 'text-indigo-500' },
  { key: 'heard', label: 'What we heard', icon: Ear, placeholder: 'Quotes, concerns, themes from conversations…', color: 'text-violet-500' },
  { key: 'needHelp', label: 'What needs help', icon: HelpCircle, placeholder: 'Blockers, gaps, escalations needed…', color: 'text-amber-500' },
] as const;

export const PulseTab: React.FC<Props> = ({ projectId, week }) => {
  const [entry, setEntry] = useState<ReachPulseEntry | null>(null);
  const [inputs, setInputs] = useState<Record<string, string>>({ saw: '', heard: '', needHelp: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    reachPulseAPI.getWeek(projectId, week).then(r => setEntry(r.data.entry ?? null));
  }, [projectId, week]);

  const list = (field: PulseField): string[] =>
    entry?.[field] ?? [];

  const add = async (field: PulseField) => {
    const val = inputs[field].trim();
    if (!val) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const updated: ReachPulseEntry = {
        week,
        saw: field === 'saw' ? [...list('saw'), val] : list('saw'),
        heard: field === 'heard' ? [...list('heard'), val] : list('heard'),
        needHelp: field === 'needHelp' ? [...list('needHelp'), val] : list('needHelp'),
        updatedAt: now,
        updatedBy: '',
      };
      await reachPulseAPI.putWeek(projectId, week, updated);
      setEntry(updated);
      setInputs(prev => ({ ...prev, [field]: '' }));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (field: PulseField, idx: number) => {
    const updated: ReachPulseEntry = {
      week,
      saw: field === 'saw' ? list('saw').filter((_, i) => i !== idx) : list('saw'),
      heard: field === 'heard' ? list('heard').filter((_, i) => i !== idx) : list('heard'),
      needHelp: field === 'needHelp' ? list('needHelp').filter((_, i) => i !== idx) : list('needHelp'),
      updatedAt: new Date().toISOString(),
      updatedBy: '',
    };
    await reachPulseAPI.putWeek(projectId, week, updated);
    setEntry(updated);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {FIELDS.map(({ key, label, icon: Icon, placeholder, color }) => {
          const items = list(key);
          return (
            <div key={key} className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden flex flex-col">
              <div className="px-4 py-3 border-b border-[#e2e8f0] flex items-center gap-2 bg-slate-50/50">
                <Icon size={15} className={color} />
                <span className="text-xs font-bold text-navy uppercase tracking-wide">{label}</span>
                <span className="ml-auto text-xs text-slate-400 font-medium">{items.length}</span>
              </div>
              <div className="flex-1 p-3 flex flex-col gap-2 min-h-[140px]">
                {items.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-6">Nothing added yet.</p>
                )}
                {items.map((item, i) => (
                  <div key={item} className="group flex items-start gap-2 text-xs text-slate-700 bg-slate-50 rounded-lg px-3 py-2">
                    <span className="flex-1 leading-relaxed">{item}</span>
                    <button
                      onClick={() => remove(key, i)}
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all flex-shrink-0 mt-0.5"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-[#e2e8f0] flex gap-2">
                <input
                  value={inputs[key]}
                  onChange={e => setInputs(prev => ({ ...prev, [key]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') add(key); }}
                  placeholder={placeholder}
                  className="flex-1 text-xs border border-[#e2e8f0] rounded-lg px-3 py-2 text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
                <button
                  onClick={() => add(key)}
                  disabled={saving || !inputs[key].trim()}
                  className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-40"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
